import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../components/atoms/Button/Button";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { signIn, ILoginData, resetSignIn } from "store/slices/auth.slice";
import { InputField } from "components";
import { useAppContext } from "utils/context/AppContext";
import { AppSchemeENUM } from "utils/enums/appENUM";
import constants from "utils/constants";

const Signin: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginData>();
  const { app } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, isSuccess, errorMessage, user } = useAppSelector(
    (state) => state.auth
  );
  let segmentvalue: string;
  try {
    if (
      window.analytics &&
      window.analytics.user &&
      typeof window.analytics.user().anonymousId === "function"
    ) {
      segmentvalue = window.analytics.user().anonymousId();
    } else {
      segmentvalue = "not available";
    }
  } catch (error) {
    segmentvalue = "blocked";
  }
  const login = (data: ILoginData) => {
    dispatch(
      signIn({
        ...data,
        appScheme: app.scheme,
        segmentAnonymousId: segmentvalue,
      })
    );
  };
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (isSuccess && token) {
      if (user) {
        window.analytics.identify(user.userId, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          company: user.organization
            ? user.organization.name
            : user.organizationName,
          companyId: user.organization ? user.organization.id : null,
          creditBalance: user.creditBalance,
          creditPurchase: user.creditPurchase || 0,
          creditUsage: user.creditUsage || 0,
          revenue: user?.revenue || 0,
        });
        if (user.isOnboarded) {
          navigate(`/dashboard`);
          return;
        }
        navigate(`/onboarding`);
      }
    }
    if (errorMessage) {
      toast.error(errorMessage, { toastId: "login-failed" });
    }
  }, [isSuccess, errorMessage]);

  useEffect(() => {
    window.analytics.page("Sign In");
  }, []);

  return (
    <div className="flex flex-col px-4 sm:px-6 md:px-40 pt-40 ">
      <div className="flex flex-col mb-7">
        <h1 className="text-2xl font-bold pb-2">Welcome</h1>
        <p className="pb-2">Please enter your email and password</p>
      </div>
      <form onSubmit={handleSubmit(login)}>
        <label className="font-semibold">Email</label>
        <InputField
          className="mt-1 mb-4"
          name="email"
          errors={errors}
          register={register}
          placeholder="Your email"
          validationSchema={{
            required: "Email is required",
            pattern: {
              value: constants.EMAIL_VALIDATION_REGEX,
              message: "Invalid email address",
            },
          }}
        />

        <label className="font-semibold">Password</label>
        <InputField
          className="mt-1 mb-4"
          type="password"
          name="password"
          errors={errors}
          register={register}
          validationSchema={{ required: "Password is required" }}
          placeholder="Your password"
        />

        <div className="flex justify-end mt-2 mb-6">
          <Link
            to={"/need-help"}
            className="text-sm text-primary-500 font-semibold"
            onClick={() => dispatch(resetSignIn())}
          >
            Need Help?
          </Link>
        </div>

        <Button
          action="submit"
          iconName="lock"
          className="w-full"
          type="primary"
          size="lg"
          isProcessing={isLoading}
          isProcessingTitle="Logging in"
        >
          Log in
        </Button>

        <div className="flex justify-center mt-2 mb-6">
          <p className="text-sm text-white-500 mb-3">Don't have an account?</p>
          <Link
            to={
              app.scheme === AppSchemeENUM.IDENTITY_MATRIX
                ? "/pricing"
                : "/signup"
            }
            className="link text-sm pl-2 text-primary-500 font-semibold"
          >
            Sign up
          </Link>
        </div>
      </form>
    </div>
  );
};
export default Signin;
